import {
  DataLayerModel,
  DataLayerPage,
  FallbackLanguage,
  PageProductCategoryGlobalField,
  Product_Product_OnlineQuoteInformationBlock,
  ProductType,
} from "@solarwinds-webbiz/types";
import { SITE_IDS } from "./DataLayer.constants";
import isMobile from "is-mobile";

export const RESOURCE_TYPES = [
  "Brochure",
  "Case Study",
  "Datasheet",
  "Demo",
  "IT Glossary",
  "Infographic",
  "Product Guide",
  "Survey",
  "Tech Tip",
  "VPAT",
  "Video",
  "Webcast",
  "Whitepaper",
  "eBook",
];

export type LanguageCode = keyof typeof LANGUAGES_AND_COUNTRIES;

export interface DataLayerProps {
  siteId: string;
  siteBrand: string;
  page: DataLayerPage;
  associatedProduct?: [ProductType] | [];
  fallbackLanguages?: FallbackLanguage[];
  pageCategory?: PageProductCategoryGlobalField["category"][];
  language?: LanguageCode;
  personalization?: {
    variation?: string;
  };
}

export enum LANGUAGES_AND_COUNTRIES {
  "en-us" = "US",
  es = "ES",
  de = "DE",
  fr = "FR",
  pt = "PT",
  ko = "KO",
  zh = "CN",
  ja = "JP",
}

export const getSiteId = (
  language: string | undefined,
  host: "try" | "swdc" | "support" | "itProDay",
) => {
  const site = SITE_IDS[host];
  const siteId = site[language as LanguageCode] || site["en-us"];

  return siteId.toString();
};

export const isTryDomain = (asPath: string, host: string) =>
  asPath.startsWith("/try/") || host.startsWith("try.");

export const getDataLayerObject = ({
  siteId,
  siteBrand,
  page,
  associatedProduct,
  pageCategory,
  language,
  fallbackLanguages,
  personalization,
}: DataLayerProps) => {
  let dlLanguage: LanguageCode = language ?? "en-us";

  if (fallbackLanguages && fallbackLanguages.length > 0) {
    dlLanguage = "en-us";
  }
  const dataLayer: DataLayerModel = {
    site: {
      id: siteId,
      brand: siteBrand,
      type: isMobile({ tablet: true, featureDetect: true })
        ? "mobile"
        : "desktop",
      isProduction: (
        process.env.NEXT_PUBLIC_ENVIRONMENT === "production"
      ).toString(),
      language: dlLanguage && (dlLanguage === "en-us" ? "en" : dlLanguage),
      country: dlLanguage ? LANGUAGES_AND_COUNTRIES[dlLanguage] : "US",
      majorVersion: "4",
    },
    page: page,
    products: [],
    personalization,
  };

  if (associatedProduct?.[0]) {
    const onlineQuoteInformation = associatedProduct?.[0]?.product?.find(
      (
        productInfo,
      ): productInfo is Product_Product_OnlineQuoteInformationBlock =>
        "online_quote_information" in productInfo,
    )?.online_quote_information;
    const associatedProductCategory =
      associatedProduct?.[0]?.main_information?.global_field?.categories;
    const category = pageCategory?.length
      ? pageCategory
      : associatedProductCategory?.length
        ? associatedProductCategory
        : [];
    dataLayer.products = [
      {
        productId: Number(onlineQuoteInformation?.online_quote_catalog_id),
        programId: Number(associatedProduct?.[0]?.main_information?.program_id),
        name: associatedProduct?.[0]?.main_information?.long_name,
        acronym: associatedProduct?.[0]?.main_information?.short_name,
        category,
        regevent:
          page.type && RESOURCE_TYPES.includes(page.type)
            ? undefined
            : associatedProduct?.[0]?.analytics?.registration_event,
      },
    ];
  }
  return dataLayer;
};
